import React from "react";
import {
  Typography,
  Button,
  Box,
  Stack,
  Grid,
  Paper,
  Container,
  Avatar,
} from "@mui/material";
import { Link } from "react-router-dom";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SavingsIcon from "@mui/icons-material/Savings";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PricingCalculator from "./PricingCalculator";
import {
  marketingContainerSx,
  marketingPageRootSx,
} from "../utils/marketingPageLayout";
import heroImage from "../images/home-phone-dumbbells.jpg";
import trialSectionImage from "../images/home-phone-gym.jpg";
import testimonialMatt from "../images/testimonial-matt-rockwell.jpg";
import testimonialChelsea from "../images/testimonial-chelsea.jpg";

const testimonials = [
  {
    quote:
      "Intuitive and flexible for individualized coaching. The team is responsive—quick to ship features and fix issues. Clients switching from other apps find it easy to use, and importing data from prior platforms saves a ton of time.",
    name: "Matt",
    title: "Rockwell Barbell · Pig Collective",
    image: testimonialMatt,
    imageAlt: "Matt, Rockwell Barbell",
  },
  {
    quote:
      "Simple for coaches and clients. Programming is faster, so I can focus on content and relationships. I use it in person and virtually—comments and the content library keep remote clients engaged. Highly recommend.",
    name: "Chelsea",
    title: "Movement Specialist",
    image: testimonialChelsea,
    imageAlt: "Chelsea",
  },
];

const featureItems = [
  {
    title: "Custom Training Plans",
    body: "Fire off personalized workouts fast.",
    icon: AssignmentTurnedInIcon,
  },
  {
    title: "Subscription & Payments",
    body: "Collect payments on time, every time.",
    icon: SavingsIcon,
  },
  {
    title: "Client Management",
    body: "Easy onboarding. Simple customization. Work on the fly.",
    icon: AccessibilityNewIcon,
  },
  {
    title: "Client Progress Tracking",
    body: "Track clients. Monitor progress. Level up your clients.",
    icon: CheckCircleOutlineIcon,
  },
];

const lightCardSx = {
  bgcolor: "#fff",
  borderRadius: 2,
  borderColor: "rgba(0,0,0,0.08)",
  boxShadow:
    "0 1px 2px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.06)",
};

const Home = () => {
  return (
    <Box
      sx={{
        ...marketingPageRootSx,
        bgcolor: "#fff",
        color: "#111",
        fontFamily: "'Roboto Mono', monospace !important",
        "& *": {
          fontFamily: "'Roboto Mono', monospace !important",
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ ...marketingContainerSx, py: { xs: 2, md: 3 } }}
      >
        <Paper variant="outlined" sx={{ overflow: "hidden", ...lightCardSx }}>
          <Grid container>
            <Grid item xs={12} md={6} sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.05rem", md: "2.85rem" },
                  fontWeight: 900,
                  lineHeight: 0.95,
                  letterSpacing: 0.5,
                  mb: 2,
                }}
              >
                JUST
                <br />
                <Box component="span" sx={{ color: "#ef0000" }}>
                  COACHING.
                </Box>
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.02rem", md: "1.2rem" },
                  fontWeight: 400,
                  mb: 3,
                }}
              >
                The personal training app for trainers who&rsquo;d rather coach
                than wrestle with software. No bloat &mdash; build programs,
                track client workouts, and run a better coaching business.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/coaching"
                  sx={{ fontWeight: 700, px: 3 }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/pricing"
                  sx={{ fontWeight: 700, px: 3 }}
                >
                  View Pricing
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={heroImage}
                alt="Trainer App workout on a phone in the gym"
                width={1024}
                height={683}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: { xs: 250, md: 370 },
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: { xs: 2, md: 3 },
            ...lightCardSx,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.45rem", md: "1.8rem" },
              fontWeight: 800,
              mb: 4,
            }}
          >
            Built by a lifter, for coaches who get it.
          </Typography>
          <Grid container spacing={{ xs: 4, md: 6 }} sx={{ mt: 3 }}>
            {featureItems.map((item) => {
              const Icon = item.icon;
              return (
                <Grid item xs={12} md={6} key={item.title}>
                  <Stack direction="row" spacing={2.25} alignItems="flex-start">
                    <Icon sx={{ color: "#ef0000", fontSize: 48, mt: 0.3 }} />
                    <Box>
                      <Typography
                        sx={{ fontSize: "1.15rem", fontWeight: 800, mb: 1.1 }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "1.08rem", lineHeight: 1.35 }}
                      >
                        {item.body}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: { xs: 2, md: 3 },
            ...lightCardSx,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.25rem", md: "1.45rem" },
              fontWeight: 800,
              mb: 3,
              textAlign: "center",
            }}
          >
            What coaches say
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {testimonials.map((t) => (
              <Grid item xs={12} md={6} key={t.name}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    borderRadius: 2,
                    bgcolor: "#fff",
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow:
                      "0 1px 2px rgba(0,0,0,0.04), 0 3px 10px rgba(0,0,0,0.05)",
                  }}
                >
                  <Avatar
                    src={t.image}
                    alt={t.imageAlt}
                    imgProps={{ loading: "lazy", decoding: "async" }}
                    sx={{
                      width: { xs: 64, sm: 72 },
                      height: { xs: 64, sm: 72 },
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "1.02rem",
                        lineHeight: 1.45,
                        fontStyle: "italic",
                        mb: 1.25,
                      }}
                    >
                      &ldquo;{t.quote}&rdquo;
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>— {t.name}</Typography>
                    {t.title ? (
                      <Typography
                        sx={{ fontSize: "0.95rem", color: "text.secondary" }}
                      >
                        {t.title}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                overflow: "hidden",
                height: "100%",
                ...lightCardSx,
              }}
            >
              <Box
                component="img"
                src={trialSectionImage}
                alt="Workout logging on a phone during training"
                width={1024}
                height={683}
                loading="lazy"
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: 280,
                  objectFit: "cover",
                }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <PricingCalculator embedded />
              <Button
                component={Link}
                to="/coaching"
                variant="contained"
                fullWidth
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  px: 5,
                  py: 2,
                }}
              >
                Start Free Trial
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Paper
          variant="outlined"
          sx={{ mt: 2, p: { xs: 3, md: 4 }, ...lightCardSx }}
        >
          <Typography
            component="h2"
            sx={{ fontSize: { xs: "1.45rem", md: "1.8rem" }, fontWeight: 800, mb: 2 }}
          >
            A personal training app built for trainers
          </Typography>
          <Typography sx={{ fontSize: "1.05rem", lineHeight: 1.5, mb: 2 }}>
            Trainer App is a personal training app for trainers and online
            fitness coaches who want to spend less time on admin and more time
            coaching. Build custom training programs, deliver workouts to your
            clients&rsquo; phones, track progress and check-ins, and collect
            recurring payments &mdash; all from one simple platform. Whether you
            coach in person, online, or both, it gives independent personal
            trainers the tools to run a real coaching business without the
            bloat of bigger software.
          </Typography>
          <Typography sx={{ fontSize: "1.05rem", lineHeight: 1.5 }}>
            From custom program design and exercise libraries to client messaging
            and progress tracking, everything a personal trainer needs to manage
            and grow a client roster lives in one place.
          </Typography>

          <Typography
            component="h3"
            sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, mt: 4, mb: 2 }}
          >
            Frequently asked questions
          </Typography>
          {[
            {
              q: "What is the best personal training app for trainers?",
              a: "The best personal training app for trainers is one that is simple to use and covers the whole coaching workflow: program building, client workout tracking, check-ins, messaging, and payments. Trainer App combines all of these in one platform without the bloat, so you can onboard clients and deliver workouts in minutes.",
            },
            {
              q: "Can I use Trainer App for online coaching?",
              a: "Yes. Trainer App is built for online and hybrid coaching. You can program workouts remotely, track client progress, run check-ins, and collect payments from anywhere, while clients log their training from their phones.",
            },
            {
              q: "Is there a free trial for personal trainers?",
              a: "Yes. You can start a free trial and only pay as your coaching business grows. There is no long-term contract required to get started.",
            },
          ].map((item) => (
            <Box key={item.q} sx={{ mb: 2.5 }}>
              <Typography
                component="h4"
                sx={{ fontSize: "1.08rem", fontWeight: 700, mb: 0.75 }}
              >
                {item.q}
              </Typography>
              <Typography sx={{ fontSize: "1.02rem", lineHeight: 1.5 }}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;
